import { useCallback } from 'react'
import { useMutation } from 'react-query'
import { useCreateMediaObjects } from 'hooks/queries/mediaObjects/useCreateMediaObjects'
import { useCreateSource } from 'hooks/queries/workspaces/useCreateSource'
import {
  useCreateBrandKitSource,
  createBrandKitSource as createBrandKitSourceApi,
  type ExistingSourceVariables,
} from 'hooks/queries/useCreateBrandKitSource'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { BrandKitSource } from 'types'

interface UseFileUploadOptions {
  skipQueryInvalidation?: boolean
}

export const useFileUpload = (brandKitId: number, options?: UseFileUploadOptions) => {
  const { skipQueryInvalidation = false } = options || {}
  const workspaceId = useCurrentWorkspaceId()
  const { mutateAsync: createMediaObjects, isLoading: isLoadingMediaObjects } = useCreateMediaObjects()
  const { mutateAsync: createSource, isLoading: isLoadingSource } = useCreateSource()
  const { mutateAsync: createBrandKitSource, isLoading: isLoadingBrandKitSource } = useCreateBrandKitSource()

  const { mutateAsync: createBrandKitSourceSilent, isLoading: isLoadingSilent } = useMutation<
    BrandKitSource,
    Error,
    ExistingSourceVariables
  >(async (vars) => {
    const result = await createBrandKitSourceApi({
      workspaceId,
      source: vars.source,
      brandKitId: vars.brandKitId,
      name: vars.name,
      emoji: vars.emoji,
    })
    return result.brand_kit_source
  })

  const createSource_ = skipQueryInvalidation ? createBrandKitSourceSilent : createBrandKitSource

  const handleUpload = useCallback(
    async (files: File[]) => {
      if (!files.length || !brandKitId) return

      const mediaObjects = await createMediaObjects(files.map((file) => ({ file, filename: file.name })))
      const brandKitSources = files.map(async (file, index) => {
        const mediaObject = mediaObjects[index]
        const source = await createSource({ media_object_id: mediaObject.id, type: 'SourceMaterialsMediaObject' })
        return createSource_({
          brandKitId,
          source: { type: 'SourceMaterialsMediaObject', id: source.source.id },
          name: file.name,
        })
      })

      return Promise.all(brandKitSources)
    },
    [brandKitId, createSource_, createSource, createMediaObjects],
  )

  return {
    handleUpload,
    isLoading:
      isLoadingMediaObjects || isLoadingSource || (skipQueryInvalidation ? isLoadingSilent : isLoadingBrandKitSource),
  }
}
